import { AuthContext } from "components/authProvider/authProvider";
import { getAuth, signOut } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import React, { useState, useEffect, useContext, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { app, host } from "../../config";
import CustomAlert from "components/generals/alert/CustomAlert";
import { DataGrid, GridColDef, GridRenderCellParams, GridToolbar, useGridApiRef } from "@mui/x-data-grid";
import { Box, Button } from "@mui/material";
import { FormSubmissionDetail, FormSubmissionDetailModal } from "./FormSubmissionDetailModal";

type FormSubmission = {
  Form_ID: string;
  Submission_ID: string;
  Form_Name: string;
  User_Email: string;
  Entry_Date: string;
};

export type ColumnHeader = {
  Column_Name: string;
  Field_Name: string;
};
export type FormSubmissionsDataGridProps = {
  columnHeaders: ColumnHeader[];
  formId: number;
  formName: string;
};

const auth = getAuth();
const db = getFirestore(app);

const FormSubmissionTable: React.FC<FormSubmissionsDataGridProps> = ({ columnHeaders, formId, formName }) => {
  const apiRef = useGridApiRef();
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [formDetailIsOpen, setFormDetailIsOpen] = useState<boolean>(false);
  const [formSubmissionDetail, setFormSubmissionDetail] = useState<FormSubmissionDetail | null>();

  const [isLoading, setIsLoading] = useState(false);

  const [isAlertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>("");

  const openAlert = () => {
    setAlertOpen(true);
  };

  const closeAlert = () => {
    setAlertOpen(false);
  };

  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error("AuthContext is undefined");
  }
  const { currentUser, role, suspended } = authContext;

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const columns: GridColDef[] = useMemo(() => {
    const columns: GridColDef[] = [
      {
        field: "Submission Details",
        headerName: "Submission Details",
        minWidth: 200,
        maxWidth: 200,
        renderCell: (params: GridRenderCellParams<any>) => (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <strong>{params.value}</strong>
            <Button
              variant="contained"
              size="small"
              style={{ margin: "auto", backgroundColor: "#004e9d" }}
              onClick={() => handleOpen(params.row)}
            >
              Submission Details
            </Button>
          </Box>
        ),
        editable: false,
        headerClassName: "super-app-theme--header",
      },
    ];

    columnHeaders.map((header) =>
      columns.push({
        field: header.Field_Name,
        headerName: header.Column_Name,
        editable: false,
        headerClassName: "super-app-theme--header",
      })
    );
    console.log("Set columns");
    return columns;
  }, []);

  const fetchSubmissions = async () => {
    setIsLoading(true);
    const data = await currentUser.getIdToken(false).then((tk) => {
      const url = `${host}/api/Reporting/FormSubmissions/getFormSubmissions/${formId}`;
      return fetch(url, {
        headers: {
          authorization: `Bearer ${tk}`,
        },
      }).then((response: Response) => {
        if (response.ok) {
          setIsLoading(false);
          return response.json().then((res) => {
            return res.data;
          });
        } else {
          setAlertMessage(
            "There was an error when fetching submissions. Please try again later and contact IT if the problem persists."
          );
          setAlertOpen(true);
          return [];
        }
      });
    });

    setSubmissions(data);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        apiRef.current?.autosizeColumns({
          includeHeaders: true,
          includeOutliers: true,
          expand: true,
          includeHeaderFilters: true,
        });
      });
    });
  };

  const handleFormDetailClose = () => {
    setFormSubmissionDetail(null);
    setFormDetailIsOpen(false);
  };

  if (suspended == true) {
    signOut(auth)
      .then(() => {
        console.log("Signed out");
        return <Navigate to="/login" />;
      })
      .catch((error) => {
        console.error("Error signing out", error);
      });
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (role !== "admin" && role !== "internal_ugp") {
    return <Navigate to="/basePage" />;
  }

  function handleOpen(row: any): void {
    setIsLoading(true);
    let params = new URLSearchParams({ submissionID: row.Submission_ID });

    currentUser.getIdToken(false).then((tk) => {
      fetch(`${host}/api/Reporting/FormSubmissions/getFormSubmissionDetail?${params}`, {
        method: "GET",
        headers: {
          authorization: `Bearer ${tk}`,
        },
      })
        .then(async (response: Response) => {
          if (response.status != 200) {
            await response.text().then((text) => {
              console.log(text);
              alert(`An error has occured when fetching the Form Submission Detail:\n${text}`);
            });
            return;
          } else {
            await response.json().then((data) => {
              const formSubmissionJSON = JSON.parse(data.formSubmissionDetail.Submission_JSON);
              setFormSubmissionDetail({
                ...data.formSubmissionDetail,
                Submission_JSON: formSubmissionJSON,
                Files: data.File_Names,
              });
              setFormDetailIsOpen(true);
            });
          }
        })
        .catch((error: any) => {
          console.log(error);
          alert("An error has occured when searching for form submission");
        })
        .finally(() => {
          setIsLoading(false);
          console.log("Processing finished");
        });
    });
  }

  return (
    <div className={"myProj-body"}>
      <h1 className={"myProj-h1"}>{formName} Form Submissions</h1>

      <CustomAlert message={alertMessage} isOpen={isAlertOpen} onClose={closeAlert} />
      <Box
        sx={{
          height: 650,
          width: "80vw", // Updated to 100%
          backgroundColor: "#f8f8f8",
          boxShadow: "0 4px 8px rgba(0,0,0,0.2)", // Enhanced shadow
          padding: "20px",
          margin: "auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          "& .MuiDataGrid-columnHeaders .super-app-theme--header": {
            backgroundColor: "#004e9d",
            color: "#ffffff",
            padding: "10px 20px",
            boxShadow: "0 3px 6px rgba(0,0,0,0.2)", // Enhanced shadow
            fontWeight: "bold",
          },
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #e0e0e0",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
            color: "#333",
          },
        }}
      >
        <DataGrid
          sx={{
            height: "auto", // Allows DataGrid to grow with content up to the Box height
            width: "100%", // Ensures DataGrid uses full width of the Box, considering padding
            "& .MuiDataGrid-virtualScroller": {
              marginTop: "0 !important", // Adjusts the header alignment issue
            },
            "& .MuiDataGrid-iconButtonContainer": {
              marginLeft: "5px",
            },
          }}
          apiRef={apiRef}
          rows={submissions}
          columns={columns}
          getRowId={(row) => row.Submission_ID}
          disableColumnSelector
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[5, 10, 50, 100]}
          slots={{
            toolbar: GridToolbar,
          }}
          showToolbar
          disableRowSelectionOnClick
          loading={isLoading}
        />
      </Box>
      <FormSubmissionDetailModal
        open={formDetailIsOpen}
        onClose={handleFormDetailClose}
        formSubmissionDetail={formSubmissionDetail}
      />
    </div>
  );
};

export default FormSubmissionTable;
